import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { X, Phone, MessageSquare, Clock, Car, Trash2, Send, Mail, ChevronDown, Calendar, AlertCircle, Eye, CheckCircle, CreditCard, Activity, ChevronUp, Copy, Package2 } from 'lucide-react';
import { Proposal, ProposalOffer, approveProposalOffer, declineProposalOffer, getProposal, deleteProposal, getProposalEvents, sendProposalLinkViaBoth, getOrCreateProposalToken, logProposalEvent } from '../lib/proposals';
import { supabase } from '../lib/supabaseClient';
import CreateBookingModal from './CreateBookingModal';
import { createTestProposalEventSimple } from '../lib/debugUtils';
import RichTextDescription from './RichTextDescription';
import ProposalEmailModal from './ProposalEmailModal';
import ProposalSMSModal from './ProposalSMSModal';
import { sendGmailEmail } from '../lib/email';
import { sendSMS } from '../lib/messaging';
import { ProductPhotoService } from '../services/productPhotoService';

interface ProposalDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  proposal: Proposal;
  onUpdate?: (proposal: Proposal) => void;
  readOnly?: boolean;
  onDelete?: () => void;
}

export interface ProposalService {
  id: string;
  proposal_id: string;
  offer_id: string;
  service_id: string;
  vehicle_id: string;
  name: string;
  description: string | null;
  price: number; // This is the original price
  discount_type?: 'percentage' | 'dollar';
  discount_value?: number;
  discount_amount: number;
  tax_exempt: boolean;
  tax_rate: number | null;
  tax_amount: number;
  total_amount: number;
  duration_minutes: number;
  order_index: number;
  created_at: string;
  updated_at: string;
  vehicle_make?: string;
  vehicle_model?: string;
  is_selected?: boolean;
  isSelectable?: boolean;
  // Legacy fields for backward compatibility
  originalPrice?: number;
  discountType?: 'percentage' | 'dollar';
  discountValue?: number;
}

export interface ProposalEvent {
  id: string;
  proposal_id: string;
  business_id: string;
  event_type: string;
  message: string | null;
  metadata: Record<string, any> | null;
  event_timestamp: string;
  created_by: string | null;
}

const getStatusConfig = (status: string) => {
  switch (status) {
    case 'draft':
      return {
        color: 'bg-gray-500',
        text: 'Draft',
        description: 'Not yet sent to customer'
      };
    case 'sent':
      return {
        color: 'bg-blue-500',
        text: 'Sent',
        description: 'Awaiting customer review'
      };
    case 'viewed':
      return {
        color: 'bg-purple-500',
        text: 'Viewed',
        description: 'Customer has viewed the proposal'
      };
    case 'approved':
      return {
        color: 'bg-green-500',
        text: 'Approved',
        description: 'Customer has approved an offer'
      };
    case 'declined':
      return {
        color: 'bg-red-500',
        text: 'Declined',
        description: 'Customer has declined all offers'
      };
    default:
      return {
        color: 'bg-gray-500',
        text: 'Unknown',
        description: ''
      };
  }
};

export default function ProposalDetailsModal({ 
  isOpen, 
  onClose,
  proposal,
  onUpdate,
  readOnly = false,
  onDelete
}: ProposalDetailsModalProps) {
  const navigate = useNavigate();
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isConvertModalOpen, setIsConvertModalOpen] = useState(false);
  const [localProposal, setLocalProposal] = useState<Proposal>(proposal);
  const [isSending, setIsSending] = useState(false);
  const [showSendOptions, setShowSendOptions] = useState(false);
  const [proposalEvents, setProposalEvents] = useState<ProposalEvent[]>([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [isHistoryExpanded, setIsHistoryExpanded] = useState(false);
  const [justSent, setJustSent] = useState(false);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [isSMSModalOpen, setIsSMSModalOpen] = useState(false);
  const [businessName, setBusinessName] = useState('your service provider');
  const [urlCopied, setUrlCopied] = useState(false);
  const [productPhotos, setProductPhotos] = useState<Record<string, string>>({});
  
  useEffect(() => {
    setLocalProposal(proposal);
    
    // Load proposal events when the modal is opened
    if (isOpen && proposal?.id) {
      console.log('Modal opened, loading events for proposal:', proposal.id);
      loadProposalEvents(proposal.id);
      
      // Load business name
      loadBusinessName(proposal.business_id);
      
      // Load product photos
      loadProductPhotos();
    }
  }, [proposal, isOpen]);

  const loadBusinessName = async (businessId: string) => {
    try {
      const { data: business, error } = await supabase
        .from('businesses')
        .select('name')
        .eq('id', businessId)
        .single();
      
      if (!error && business?.name) {
        setBusinessName(business.name);
      }
    } catch (error) {
      console.error('Error fetching business name:', error);
    }
  };

  const loadProductPhotos = async () => {
    try {
      // Collect all product stock_item_ids from all offers
      const stockItemIds: string[] = [];
      
      proposal.offers?.forEach(offer => {
        (offer as any).products?.forEach((product: any) => {
          if (product.stock_item_id && !stockItemIds.includes(product.stock_item_id)) {
            stockItemIds.push(product.stock_item_id);
          }
        });
      });
      
      if (stockItemIds.length > 0) {
        const photoMap = await ProductPhotoService.getPhotosByStockItemIds(stockItemIds);
        const photoUrls: Record<string, string> = {};
        
        if (photoMap && typeof photoMap === 'object') {
          Object.entries(photoMap).forEach(([itemId, photo]) => {
            if (photo && photo.photo_url) {
              photoUrls[itemId] = photo.photo_url;
            }
          });
        }
        
        setProductPhotos(photoUrls);
      }
    } catch (error) {
      console.error('Error loading product photos:', error);
      setProductPhotos({});
    }
  };

  // Component for product icon - shows photo if available, otherwise green Package2 icon
  const ProductIcon = ({ product }: { product: any }) => {
    const photoUrl = productPhotos[product.stock_item_id];
    
    if (photoUrl) {
      return (
        <div className="h-12 w-12 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center flex-shrink-0">
          <img 
            src={photoUrl} 
            alt={product.name}
            className="w-full h-full object-cover"
            onError={(e) => {
              // If image fails to load, show fallback icon
              e.currentTarget.style.display = 'none';
              const parent = e.currentTarget.parentElement;
              if (parent) {
                parent.innerHTML = '<div class="h-6 w-6 text-green-600"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16.5 9.4 7.55 4.24"/><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.29,7 12,12 20.71,7"/><line x1="12" x2="12" y1="22" y2="12"/></svg></div>';
                parent.className = "h-12 w-12 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0";
              }
            }}
          />
        </div>
      );
    }
    
    return (
      <div className="h-12 w-12 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
        <Package2 className="h-6 w-6 text-green-600" />
      </div>
    );
  };
  
  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Close send options dropdown when clicking outside
  useEffect(() => {
    const closeDropdown = () => {
      setShowSendOptions(false);
    };

    if (showSendOptions) {
      document.addEventListener('click', closeDropdown);
    }

    return () => {
      document.removeEventListener('click', closeDropdown);
    };
  }, [showSendOptions]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
    }
  };

  const handleApproveOffer = async (offer: ProposalOffer) => {
    if (!localProposal.id) return;
    
      setLoading(true);
    try {
      await approveProposalOffer(offer.id);
      
      if (onUpdate) {
        const updatedProposal = await getProposal(localProposal.id);
        setLocalProposal(updatedProposal);
        onUpdate(updatedProposal);
      }
    } catch (error) {
      console.error('Error approving offer:', error);
      setError('Failed to approve offer');
    } finally {
      setLoading(false);
    }
  };

  const handleDeclineOffer = async (offer: ProposalOffer) => {
    if (!localProposal.id) return;
    
      setLoading(true);
    try {
      await declineProposalOffer(offer.id);
      
      if (onUpdate) {
        const updatedProposal = await getProposal(localProposal.id);
        setLocalProposal(updatedProposal);
        onUpdate(updatedProposal);
      }
    } catch (error) {
      console.error('Error declining offer:', error);
      setError('Failed to decline offer');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!localProposal.id) return;
      
    const confirmed = window.confirm('Are you sure you want to delete this proposal? This action cannot be undone.');
    if (!confirmed) return;
      
    setLoading(true);
    try {
      await deleteProposal(localProposal.id);
      onClose();
      if (onDelete) {
        onDelete();
      }
    } catch (error) {
      console.error('Error deleting proposal:', error);
      setError('Failed to delete proposal');
    } finally {
      setLoading(false);
    }
  };

  const handleSendText = async () => {
    if (!localProposal.id) return;
    
    const customerPhone = localProposal.customer?.phone || '';
    
    if (!customerPhone) {
      setError('Customer phone number is missing');
      return;
    }
    
    setShowSendOptions(false);
    setIsSMSModalOpen(true);
  };

  const handleSMSSend = async (message: string) => {
    if (!localProposal.id) return;
    
    setIsSending(true);
    
    try {
      const customerPhone = localProposal.customer?.phone || '';
      const businessId = localProposal.business_id;
      
      // Send SMS using our messaging service with custom message
      const smsResult = await sendSMS({
        businessId,
        to: customerPhone,
        message: message
      });

      if (!smsResult.success) {
        throw new Error(smsResult.error || 'Failed to send SMS');
      }

      console.log('SMS sent successfully');
      setJustSent(true);

      // Update proposal status to 'sent'
      const { error: updateError } = await supabase
        .from('proposals')
        .update({ status: 'sent' })
        .eq('id', localProposal.id);

      if (updateError) {
        console.error('Error updating proposal status:', updateError);
      }

      // Log SMS sent event
      await logProposalEvent(
        localProposal.id, 
        businessId, 
        'sent_sms', 
        'Proposal sent via SMS',
        {
          to_phone: customerPhone,
          custom_message: message
        }
      );
      
      // Reload events after sending
      await loadProposalEvents(localProposal.id);
      
      // Reset justSent after 3 seconds
      setTimeout(() => setJustSent(false), 3000);
      
      if (onUpdate) {
        const updatedProposal = await getProposal(localProposal.id);
        setLocalProposal(updatedProposal);
        onUpdate(updatedProposal);
      }
    } catch (error) {
      console.error('Error sending proposal:', error);
      setError('Failed to send proposal via SMS');
    } finally {
      setIsSending(false);
    }
  };

  const handleSendEmail = async () => {
    if (!localProposal.id) return;
    
    const customerEmail = localProposal.customer?.email || '';
    
    if (!customerEmail) {
      setError('Customer email is missing');
      return;
    }
    
    setShowSendOptions(false);
    setIsEmailModalOpen(true);
  };

  const handleEmailSend = async (subject: string, message: string) => {
    if (!localProposal.id) return;
    
    setIsSending(true);
    
    try {
      const customerEmail = localProposal.customer?.email || '';
      const businessId = localProposal.business_id;
      
      // Get or create a token for this proposal to generate the URL
      const tokenData = await getOrCreateProposalToken(localProposal.id, businessId);
      const proposalUrl = `${window.location.origin}/proposal/${tokenData}`;
      
      // Replace the placeholder URL in the message with the actual URL
      const finalMessage = message.replace(/\${proposalUrl}/g, proposalUrl);
      
      // Send email using our Gmail integration with custom subject and message
      const emailResult = await sendGmailEmail({
        businessId,
        to: customerEmail,
        subject: subject,
        body: finalMessage
      });

      if (!emailResult.success) {
        throw new Error(emailResult.error || 'Failed to send email');
      }

      console.log('Email sent successfully');
      setJustSent(true);

      // Update proposal status to 'sent'
      const { error: updateError } = await supabase
        .from('proposals')
        .update({ status: 'sent' })
        .eq('id', localProposal.id);

      if (updateError) {
        console.error('Error updating proposal status:', updateError);
      }

      // Log email sent event
      await logProposalEvent(
        localProposal.id, 
        businessId, 
        'sent_email', 
        'Proposal sent via email',
        {
          to_email: customerEmail,
          subject: subject,
          custom_message: finalMessage,
          url: proposalUrl
        }
      );
      
      // Reload events after sending
      await loadProposalEvents(localProposal.id);
      
      // Reset justSent after 3 seconds
      setTimeout(() => setJustSent(false), 3000);
      
      if (onUpdate) {
        const updatedProposal = await getProposal(localProposal.id);
        setLocalProposal(updatedProposal);
        onUpdate(updatedProposal);
      }
    } catch (error) {
      console.error('Error sending proposal:', error);
      setError('Failed to send proposal via email');
    } finally {
      setIsSending(false);
    }
  };

  const handleSendBoth = async () => {
    if (!localProposal.id) return;
    
    setIsSending(true);
    setShowSendOptions(false);
    
    try {
      const customerEmail = localProposal.customer?.email || '';
      const customerPhone = localProposal.customer?.phone || '';
      const businessId = localProposal.business_id;
      
      if (!customerEmail && !customerPhone) {
        setError('Customer email and phone are both missing');
        return;
      }
      
      if (!customerEmail) {
        setError('Customer email is missing');
        return;
      }
      
      if (!customerPhone) {
        setError('Customer phone number is missing');
        return;
      }
      
      await sendProposalLinkViaBoth(localProposal.id, businessId, customerEmail, customerPhone);
      setJustSent(true);
      
      // Reload events after sending
      await loadProposalEvents(localProposal.id);
      
      // Reset justSent after 3 seconds
      setTimeout(() => setJustSent(false), 3000);
      
      if (onUpdate) {
        const updatedProposal = await getProposal(localProposal.id);
        setLocalProposal(updatedProposal);
        onUpdate(updatedProposal);
      }
    } catch (error) {
      console.error('Error sending proposal:', error);
      setError('Failed to send proposal via both methods');
    } finally {
      setIsSending(false);
    }
  };

  const handleCopyUrl = async () => {
    try {
      setIsSending(true);
      setError(null);
      setShowSendOptions(false);

      const businessId = localProposal.business_id;
      if (!businessId) {
        setError('Business ID not found');
        return;
      }

      // Get or create a token for this proposal
      const tokenData = await getOrCreateProposalToken(localProposal.id, businessId);
      
      // Construct the public URL
      const publicUrl = `${window.location.origin}/proposal/${tokenData}`;
      
      // Copy to clipboard
      await navigator.clipboard.writeText(publicUrl);
      
      // Log the proposal URL copied event
      await logProposalEvent(
        localProposal.id, 
        businessId, 
        'url_copied', 
        'Proposal URL copied to clipboard',
        {
          url: publicUrl,
          copied_at: new Date().toISOString()
        }
      );
      
      // Update proposal status to 'sent' (same as other send methods)
      const { error: updateError } = await supabase
        .from('proposals')
        .update({ status: 'sent' })
        .eq('id', localProposal.id);

      if (updateError) {
        console.error('Error updating proposal status:', updateError);
        setError('Failed to update proposal status');
        return;
      }
      
      setUrlCopied(true);
      setJustSent(true);
      
      // Reload events after copying URL
      await loadProposalEvents(localProposal.id);
      
      // Reset the copied state after 2 seconds
      setTimeout(() => {
        setUrlCopied(false);
      }, 2000);

      // Reset justSent after 3 seconds (same as other send methods)
      setTimeout(() => setJustSent(false), 3000);
      
      // Update the proposal status and notify parent component
      if (onUpdate) {
        const updatedProposal = await getProposal(localProposal.id);
        setLocalProposal(updatedProposal);
        onUpdate(updatedProposal);
      }

    } catch (error) {
      console.error('Error copying proposal URL:', error);
      setError('Failed to copy proposal URL');
    } finally {
      setIsSending(false);
    }
  };

  const loadProposalEvents = async (proposalId: string) => {
      setLoadingEvents(true);
    console.log('Loading events for proposal:', proposalId);
      
    try {
      const events = await getProposalEvents(proposalId);
      console.log('Loaded events:', events);
        setProposalEvents(events);
    } catch (error) {
      console.error('Error loading proposal events:', error);
      setError('Failed to load proposal events');
    } finally {
      setLoadingEvents(false);
    }
  };

  const createTestEvent = async () => {
    if (!localProposal.id) return;
    
    try {
      await createTestProposalEventSimple(localProposal.id);
      console.log('Test event created successfully');
      
      // Reload events after creating test event
      await loadProposalEvents(localProposal.id);
    } catch (error) {
      console.error('Error creating test event:', error);
      setError('Failed to create test event');
    }
  };

  const getEventTypeInfo = (eventType: string) => {
    switch (eventType) {
      case 'sent_sms':
        return { 
          icon: <MessageSquare className="h-4 w-4 text-blue-600" />, 
          label: 'Text Message Sent', 
          color: 'text-blue-800' 
        };
      case 'sent_email':
        return { 
          icon: <Mail className="h-4 w-4 text-blue-600" />, 
          label: 'Email Sent', 
          color: 'text-blue-800' 
        };
      case 'viewed':
        return { 
          icon: <Eye className="h-4 w-4 text-purple-600" />, 
          label: 'Viewed by Customer', 
          color: 'text-purple-800' 
        };
      case 'offer_approved':
        return { 
          icon: <CheckCircle className="h-4 w-4 text-green-600" />, 
          label: 'Offer Approved', 
          color: 'text-green-800' 
        };
      case 'offer_declined':
        return { 
          icon: <X className="h-4 w-4 text-red-600" />, 
          label: 'Offer Declined', 
          color: 'text-red-800' 
        };
      case 'paid':
        return { 
          icon: <CreditCard className="h-4 w-4 text-green-600" />, 
          label: 'Payment Received', 
          color: 'text-green-800' 
        };
      case 'booked':
        return { 
          icon: <Calendar className="h-4 w-4 text-green-600" />, 
          label: 'Converted to Booking', 
          color: 'text-green-800' 
        };
      case 'test_event':
        return { 
          icon: <Activity className="h-4 w-4 text-gray-600" />, 
          label: 'Test Event', 
          color: 'text-gray-800' 
        };
      case 'url_copied':
        return { 
          icon: <Copy className="h-4 w-4 text-blue-600" />, 
          label: 'URL Copied', 
          color: 'text-blue-800' 
        };
      default:
        console.log('Unknown event type:', eventType);
        return { 
          icon: <Activity className="h-4 w-4 text-gray-600" />, 
          label: eventType, 
          color: 'text-gray-800' 
        };
    }
  };

  if (!isOpen && !isAnimating) return null;
  if (!localProposal) return null;

  const modalContent = (
    <>
      <div 
        className={`fixed inset-0 transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0'
        }`}
        style={{ 
          zIndex: 9999 // High z-index - Portal renders to document.body so no stacking context issues
        }}
        onTransitionEnd={handleTransitionEnd}
      >
        <div 
          className={`fixed inset-0 ios-modal-content transition-colors duration-200 ${
            isOpen ? 'bg-gray-50' : 'bg-black'
          }`}
          style={{ 
            WebkitBackfaceVisibility: 'hidden',
            zIndex: 99950,
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100%',
            height: '100%',
            display: isOpen ? 'block' : 'none',
            paddingTop: 'env(safe-area-inset-top)',
            paddingBottom: 'env(safe-area-inset-bottom)',
            paddingLeft: 'env(safe-area-inset-left)',
            paddingRight: 'env(safe-area-inset-right)'
          }}
        >
          <div className="h-screen flex flex-col">
            {/* Header */}
            <div className="bg-gray-900 border-b border-gray-800">
              <div className="flex items-center justify-between px-6 py-4">
                <div className="flex items-center space-x-3">
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    <X className="h-6 w-6" />
                  </button>
                  <div>
                    <h2 className="text-xl font-semibold text-white">
                      {localProposal.title}
                    </h2>
                    <div className="flex items-center mt-1">
                      <div className={`h-2 w-2 rounded-full ${getStatusConfig(localProposal.status).color} mr-2`} />
                      <span className="text-sm text-gray-300">{getStatusConfig(localProposal.status).description}</span>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  {/* Send Button */}
                  {!readOnly && (localProposal.status === 'draft' || localProposal.status === 'sent' || localProposal.status === 'viewed') && (
                    <div className="relative">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowSendOptions(!showSendOptions);
                        }}
                        disabled={isSending || justSent}
                        className={`inline-flex items-center justify-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                          justSent 
                            ? 'text-green-700 bg-green-100 hover:bg-green-200' 
                            : 'text-white bg-blue-600 hover:bg-blue-700'
                        }`}
                        title={isSending ? 'Sending...' : justSent ? 'Sent!' : localProposal.status === 'draft' ? 'Send Proposal' : 'Send Again'}
                      >
                        {isSending ? (
                          <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                        ) : justSent ? (
                          <CheckCircle className="h-4 w-4" />
                        ) : (
                          <Send className="h-4 w-4" />
                        )}
                        <span className="ml-2">
                          {isSending ? 'Sending...' : justSent ? 'Sent!' : 'Send'}
                        </span>
                      </button>
                      
                      {showSendOptions && (
                        <div 
                          className="absolute right-0 top-full mt-2 w-52 rounded-md shadow-lg bg-white border border-gray-200 z-20"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="py-1" role="menu" aria-orientation="vertical">
                            <button
                              onClick={handleSendText}
                              className="w-full px-4 py-3 text-sm text-left text-gray-700 hover:bg-gray-50 flex items-center transition-all duration-200 active:scale-95 touch-manipulation"
                              style={{ minHeight: '44px' }}
                              role="menuitem"
                            >
                              <MessageSquare className="h-4 w-4 mr-3 text-gray-500" />
                              SMS
                            </button>
                            <button
                              onClick={handleSendEmail}
                              className="w-full px-4 py-3 text-sm text-left text-gray-700 hover:bg-gray-50 flex items-center transition-all duration-200 active:scale-95 touch-manipulation"
                              style={{ minHeight: '44px' }}
                              role="menuitem"
                            >
                              <Mail className="h-4 w-4 mr-3 text-gray-500" />
                              Email
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Delete Button */}
                  {!readOnly && localProposal.status !== 'booked' && (
                    <button
                      onClick={handleDelete}
                      disabled={loading}
                      className="inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50"
                      title="Delete Proposal"
                    >
                      <Trash2 className="h-4 w-4" />
                      <span className="ml-2">Delete</span>
                    </button>
                  )}
                </div>
              </div>
              
                            {/* Mobile Action Buttons - Full Width */}
              {((!readOnly && localProposal.status === 'approved') || localProposal.status === 'booked') && (
                <div className="px-2 pb-2 space-y-1 sm:px-4 sm:pb-4 sm:space-y-2">
                  {!readOnly && localProposal.status === 'approved' && (
                    <button
                      onClick={() => {
                        setIsConvertModalOpen(true);
                      }}
                      className="w-full inline-flex items-center justify-center px-6 py-4 text-base font-bold text-white bg-gradient-to-r from-green-600 to-green-700 rounded-2xl hover:from-green-700 hover:to-green-800 transition-all duration-300 ease-out transform hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-green-600/25 touch-manipulation"
                    >
                      <Calendar className="h-5 w-5 mr-3" />
                      Convert to Booking
                    </button>
                  )}
                  {localProposal.status === 'booked' && (
                    <div className="w-full flex items-center justify-center px-6 py-4 text-base font-bold text-green-400 bg-green-900/50 rounded-2xl shadow-sm border border-green-800/50">
                      <CheckCircle className="h-5 w-5 mr-3" />
                      Converted to Booking
                    </div>
                  )}
                </div>
              )}
            </div>


            {/* Content */}
            <div className="flex-1 overflow-y-auto" style={{ 
              WebkitOverflowScrolling: 'touch',
              paddingBottom: 'env(safe-area-inset-bottom, 0px)'
            }}>
              <div className="max-w-5xl mx-auto py-6 px-6" style={{
                paddingBottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))'
              }}>
                {error && (
                  <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                    {error}
                  </div>
                )}

                <div className="space-y-6">
                  {/* Customer Info */}
                  <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
                    <div className="bg-gray-50 border-b border-gray-200 px-6 py-4">
                      <h3 className="text-lg font-semibold text-gray-900">Customer</h3>
                    </div>
                    <div className="p-6">
                      <div className="flex items-start justify-between">
                        {/* Customer info */}
                        <div className="flex items-start space-x-4">
                          <div className="h-12 w-12 rounded-full bg-blue-500 flex items-center justify-center text-white font-medium text-sm">
                            {localProposal.customer?.first_name[0]}{localProposal.customer?.last_name[0]}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-base font-medium text-gray-900 truncate">
                              {localProposal.customer?.first_name} {localProposal.customer?.last_name}
                            </div>
                            {/* Mobile: Stack vertically with phone first, Desktop: Horizontal */}
                            <div className="flex flex-col space-y-1 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-4 text-sm text-gray-600">
                              {localProposal.customer?.phone && (
                                <div className="flex items-center space-x-2">
                                  <Phone className="h-4 w-4 text-gray-400" />
                                  <span className="truncate">{localProposal.customer.phone}</span>
                                </div>
                              )}
                              {localProposal.customer?.email && (
                                <div className="flex items-center space-x-2">
                                  <Mail className="h-4 w-4 text-gray-400" />
                                  <span className="truncate">{localProposal.customer.email}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        {/* Action buttons */}
                        <div className="flex items-center space-x-2 flex-shrink-0">
                          {/* Phone icon */}
                          {localProposal.customer?.phone && (
                            <button 
                              className="p-3 -m-1 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-all active:scale-95"
                              onClick={() => window.open(`tel:${localProposal.customer?.phone}`, '_self')}
                              title={`Call ${localProposal.customer?.phone}`}
                            >
                              <Phone className="h-5 w-5" />
                            </button>
                          )}
                          
                          {/* Message icon */}
                          <button 
                            className="p-3 -m-1 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-all active:scale-95"
                            onClick={() => {
                              // Detect current page to pass as source
                              const currentPath = window.location.pathname;
                              let sourcePage = 'proposals'; // default for proposals
                              if (currentPath === '/calendarV2') {
                                sourcePage = 'calendar';
                              } else if (currentPath === '/crm') {
                                sourcePage = 'crm';
                              }
                              
                              navigate(`/messages?customerId=${localProposal.customer_id}&proposalId=${localProposal.id}${sourcePage ? `&source=${sourcePage}` : ''}`);
                            }}
                            title="Open messages for this customer"
                          >
                            <MessageSquare className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Decline Reason - Only show if proposal is declined and has decline reason */}
                  {localProposal.status === 'declined' && localProposal.notes && (() => {
                    try {
                      const notesData = JSON.parse(localProposal.notes);
                      const declineReason = notesData.decline_reason;
                      
                      if (declineReason) {
                        return (
                          <div className="bg-white rounded-lg border border-red-200 overflow-hidden shadow-sm">
                            <div className="bg-red-50 border-b border-red-200 px-6 py-4">
                              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                                <AlertCircle className="h-5 w-5 mr-2 text-red-600" />
                                Decline Reason
                              </h3>
                            </div>
                            <div className="p-6">
                              <p className="text-sm text-gray-800 bg-red-50 p-4 rounded-lg border border-red-200 italic">
                                "{declineReason}"
                              </p>
                              <p className="text-xs text-gray-600 mt-3">
                                The customer provided this reason when declining the proposal.
                              </p>
                            </div>
                          </div>
                        );
                      }
                    } catch (e) {
                      // If notes is not valid JSON or doesn't contain decline_reason, don't show anything
                      return null;
                    }
                    return null;
                  })()}

                  {/* Proposal History */}
                  <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
                    <div className="bg-gray-50 border-b border-gray-200 px-6 py-4 cursor-pointer hover:bg-gray-100 transition-colors"
                         onClick={() => setIsHistoryExpanded(!isHistoryExpanded)}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <h3 className="text-lg font-semibold text-gray-900">History</h3>
                          {proposalEvents.length > 0 && (
                            <span className="bg-blue-100 text-blue-700 rounded-full px-3 py-1 text-sm font-medium">
                              {proposalEvents.length}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center">
                          {isHistoryExpanded ? (
                            <ChevronUp className="h-5 w-5 text-gray-500 transition-transform duration-200" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-gray-500 transition-transform duration-200" />
                          )}
                        </div>
                      </div>
                    </div>
                    {isHistoryExpanded && (
                      <div className="p-6">
                        {loadingEvents ? (
                          <div className="text-center py-8">
                            <div className="inline-block h-8 w-8 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin mb-4"></div>
                            <p className="text-sm text-gray-600 font-medium">Loading history...</p>
                          </div>
                        ) : proposalEvents.length === 0 ? (
                          <div>
                            <p className="text-sm text-gray-600 italic">No history records available yet.</p>
                            <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                              <p className="text-sm text-gray-800 font-semibold mb-2">Diagnostic Info:</p>
                              <p className="text-xs text-gray-600 mb-1">Proposal ID: {localProposal.id}</p>
                              <p className="text-xs text-gray-600 mb-1">Proposal Status: {localProposal.status}</p>
                              <p className="text-xs text-gray-600 mb-3">Business ID: {localProposal.business_id}</p>
                              <div className="flex flex-col sm:flex-row gap-2">
                                <button 
                                  onClick={() => localProposal?.id && loadProposalEvents(localProposal.id)}
                                  className="flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                                >
                                  Retry Load Events
                                </button>
                                <button 
                                  onClick={createTestEvent}
                                  className="flex items-center justify-center px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors text-sm font-medium"
                                >
                                  Create Test Event
                                </button>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="max-h-64 overflow-y-auto space-y-3">
                            {proposalEvents.map((event) => {
                              const eventInfo = getEventTypeInfo(event.event_type);
                              
                              // Determine background color based on event type
                              let bgColorClass = "bg-gray-50";
                              if (event.event_type === 'sent_sms' || event.event_type === 'sent_email' || event.event_type === 'url_copied') {
                                bgColorClass = "bg-blue-50";
                              } else if (event.event_type === 'offer_approved') {
                                bgColorClass = "bg-green-50";
                              } else if (event.event_type === 'offer_declined') {
                                bgColorClass = "bg-red-50";
                              } else if (event.event_type === 'viewed') {
                                bgColorClass = "bg-purple-50";
                              } else if (event.event_type === 'test_event') {
                                bgColorClass = "bg-gray-50";
                              }
                              
                              return (
                                <div 
                                  key={event.id} 
                                  className={`rounded-lg border border-gray-200 p-4 ${bgColorClass} hover:shadow-sm transition-shadow`}
                                >
                                    <div className="flex">
                                    <div className="mr-3 mt-1 flex-shrink-0">
                                        {eventInfo.icon}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                      <div className="flex justify-between items-start mb-2">
                                        <span className={`text-sm font-bold ${eventInfo.color} truncate mr-2`}>{eventInfo.label}</span>
                                        <span className="text-xs text-gray-600 whitespace-nowrap bg-white px-2 py-1 rounded-lg border border-gray-200 flex-shrink-0 font-medium">
                                            {new Date(event.event_timestamp).toLocaleTimeString('en-US', {
                                              hour: '2-digit', 
                                              minute: '2-digit',
                                              hour12: true
                                            })}
                                          </span>
                                        </div>
                                        
                                      {/* Enhanced event content */}
                                      <div className="text-sm">
                                          {event.message && (
                                          <p className="text-gray-700 mb-2">{event.message}</p>
                                          )}
                                          
                                        {/* Enhanced metadata display */}
                                          {event.metadata && Object.keys(event.metadata).length > 0 && (
                                          <div className="text-sm text-gray-600">
                                              {event.event_type === 'sent_sms' && event.metadata.to_phone && (
                                                <div className="flex items-center">
                                                <Phone className="h-4 w-4 mr-2 text-blue-600" />
                                                <span className="font-medium">{event.metadata.to_phone}</span>
                                                </div>
                                              )}
                                              {event.event_type === 'sent_email' && event.metadata.to_email && (
                                                <div className="flex items-center">
                                                <Mail className="h-4 w-4 mr-2 text-blue-600" />
                                                <span className="font-medium">{event.metadata.to_email}</span>
                                                </div>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Offers */}
                  {localProposal.offers?.map((offer) => {
                    // Determine if this offer should be greyed out
                    const isProposalApproved = localProposal.status === 'approved';
                    const isProposalDeclined = localProposal.status === 'declined';
                    const isOfferSelected = offer.status === 'approved';
                    const isOfferGreyedOut = (isProposalApproved && !isOfferSelected) || isProposalDeclined;
                    
                    return (
                      <div 
                        key={offer.id} 
                        className={`bg-white rounded-lg border overflow-hidden shadow-sm transition-all ${
                          isOfferGreyedOut 
                            ? 'opacity-60 border-gray-200' 
                            : isOfferSelected 
                              ? 'border-green-300 ring-2 ring-green-200' 
                              : 'border-gray-200'
                        }`}
                      >
                        <div className={`px-6 py-4 border-b flex justify-between items-center ${
                          isOfferGreyedOut 
                            ? 'bg-gray-50 border-gray-200' 
                            : isOfferSelected 
                              ? 'bg-green-50 border-green-200' 
                              : 'bg-gray-50 border-gray-200'
                        }`}>
                          <div className="flex items-center space-x-3">
                            <h3 className={`text-base font-bold ${
                              isOfferGreyedOut ? 'text-gray-600' : isOfferSelected ? 'text-green-800' : 'text-gray-900'
                            }`}>
                              {offer.title}
                            </h3>
                          {offer.status === 'approved' && (
                            <span className="px-3 py-1 text-sm font-medium rounded-full bg-green-100 text-green-800">
                              Selected by Customer
                            </span>
                          )}
                          {offer.status === 'declined' && (
                            <span className="px-3 py-1 text-sm font-medium rounded-full bg-gray-100 text-gray-700">
                              Not Selected
                            </span>
                          )}
                          {localProposal.status === 'declined' && (
                            <span className="px-3 py-1 text-sm font-medium rounded-full bg-red-100 text-red-700">
                              Declined by Customer
                            </span>
                          )}
                        </div>
                        {offer.status === 'pending' && !readOnly && localProposal.status !== 'declined' && (
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleApproveOffer(offer)}
                              className="inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                            >
                              <CheckCircle className="h-4 w-4 mr-1.5" />
                              Approve
                            </button>
                            <button
                              onClick={() => handleDeclineOffer(offer)}
                              className="inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100 disabled:opacity-50 transition-colors"
                            >
                              <X className="h-4 w-4 mr-1.5" />
                              Decline
                            </button>
                          </div>
                        )}
                      </div>
                        <div className="divide-y divide-gray-200">
                        {offer.services?.filter(service => 
                          // Only show selected services for approved offers, show all for other statuses
                          offer.status !== 'approved' || (service as any).is_selected === true
                          ).map((service) => {
                            // Determine service styling based on selection state
                            const isServiceSelectable = (service as any).isSelectable;
                            const isServiceSelected = (service as any).is_selected;
                            const shouldGreyOutService = isOfferGreyedOut || (isServiceSelectable && !isServiceSelected && isProposalApproved);
                            const shouldHighlightService = isServiceSelectable && isServiceSelected && isProposalApproved;

                            return (
                              <div 
                                key={service.id} 
                                className={`p-6 transition-all ${
                                  shouldGreyOutService 
                                    ? 'opacity-50 bg-gray-50' 
                                    : shouldHighlightService 
                                      ? 'bg-green-50 border-l-4 border-green-500' 
                                      : ''
                                }`}
                              >
                                <div className="flex items-start justify-between mb-3">
                                  <div className="flex items-start">
                                <div className="flex-1">
                                      <div className="flex items-center flex-wrap gap-2">
                                        <span className={`text-base font-bold ${
                                          shouldGreyOutService ? 'text-gray-500' : shouldHighlightService ? 'text-green-800' : 'text-gray-900'
                                        }`}>
                                          {service.name}
                                        </span>
                                        {isServiceSelectable && (
                                          <span className={`px-3 py-1 text-sm font-medium rounded-full ${
                                            shouldGreyOutService 
                                              ? 'bg-gray-100 text-gray-600' 
                                              : shouldHighlightService 
                                                ? 'bg-green-100 text-green-800' 
                                                : 'bg-blue-100 text-blue-800'
                                          }`}>
                                            Optional {isServiceSelected ? '(Selected)' : ''}
                                          </span>
                                        )}
                                  </div>
                                      <div className={`text-sm mt-2 ${shouldGreyOutService ? 'text-gray-400' : 'text-gray-600'}`}>
                                    <RichTextDescription 
                                      description={service.description} 
                                          className={`prose-sm ${
                                            shouldGreyOutService 
                                              ? 'text-gray-400 prose-headings:text-gray-400 prose-strong:text-gray-400 prose-em:text-gray-300' 
                                              : 'text-gray-600 prose-headings:text-gray-700 prose-strong:text-gray-700 prose-em:text-gray-500'
                                          }`}
                                    />
                                  </div>
                                </div>
                              </div>
                                                                <div className={`ml-4 ${
                                shouldGreyOutService ? 'text-gray-500' : shouldHighlightService ? 'text-green-800' : 'text-gray-900'
                              }`}>
                            {service.discount_amount && service.discount_amount > 0 ? (
                              <div className="text-right">
                                <div className="text-sm text-gray-500 line-through">
                                  ${service.price.toLocaleString()}
                                </div>
                                <div className="text-base font-bold text-green-600">
                                  ${(service.price - service.discount_amount).toLocaleString()}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {((service as any).discount_type || service.discountType) === 'percentage' ? `${((service as any).discount_value || service.discountValue)}% off` : `$${service.discount_amount} off`}
                                </div>
                              </div>
                            ) : (
                              <div className="text-base font-bold">
                                ${service.price.toLocaleString()}
                              </div>
                            )}
                          </div>
                            </div>
                                <div className={`flex items-center justify-between text-sm ${
                                  shouldGreyOutService ? 'text-gray-400' : 'text-gray-600'
                                }`}>
                              <div className="flex items-center">
                                    <Clock className="h-4 w-4 mr-2" />
                                    <span className="font-medium">
                                {service.duration_minutes >= 1440 
                                  ? `${Math.round(service.duration_minutes / 1440)} days`
                                  : `${Math.round(service.duration_minutes / 60)} hours`
                                }
                                    </span>
                              </div>
                              <div className="flex items-center">
                                    <span className="font-medium">
                                {service.vehicle_make && service.vehicle_model 
                                  ? `${service.vehicle_make} ${service.vehicle_model}`
                                  : 'Vehicle details not available'
                                }
                                    </span>
                              </div>
                            </div>
                          </div>
                            );
                          })}

                        {/* Products Section */}
                        {(offer as any).products?.map((product: any) => {
                            // Determine product styling based on selection state
                            const isProductSelectable = product.isSelectable;
                            const isProductSelected = product.is_selected;
                            const shouldGreyOutProduct = isOfferGreyedOut || (isProductSelectable && !isProductSelected && isProposalApproved);
                            const shouldHighlightProduct = isProductSelectable && isProductSelected && isProposalApproved;

                            return (
                              <div 
                                key={product.id} 
                                className={`p-6 transition-all border-l-4 border-green-500 ${
                                  shouldGreyOutProduct 
                                    ? 'opacity-50 bg-gray-50' 
                                    : shouldHighlightProduct 
                                      ? 'bg-green-50 border-l-4 border-green-500' 
                                      : 'bg-green-50/30'
                                }`}
                              >
                                <div className="flex items-start justify-between mb-3">
                                  <div className="flex items-start">
                                    <div className="flex-shrink-0 mr-4">
                                      <ProductIcon product={product} />
                                    </div>
                                    <div className="flex-1">
                                      <div className="flex items-center flex-wrap gap-2">
                                        <span className={`text-base font-bold ${
                                          shouldGreyOutProduct ? 'text-gray-500' : shouldHighlightProduct ? 'text-green-800' : 'text-gray-900'
                                        }`}>
                                          {product.name}
                                        </span>
                                        {isProductSelectable && (
                                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                            Optional
                                          </span>
                                        )}
                                      </div>
                                      
                                      {product.discount_amount > 0 && (
                                        <div className={`text-sm mt-1 ${
                                          shouldGreyOutProduct ? 'text-gray-500' : 'text-green-600'
                                        }`}>
                                          Discount: -${product.discount_amount.toLocaleString()}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  
                                  <div className="text-right">
                                    <div className={`text-lg font-bold ${
                                      shouldGreyOutProduct ? 'text-gray-500' : shouldHighlightProduct ? 'text-green-800' : 'text-gray-900'
                                    }`}>
                                      ${(product.price * product.quantity).toLocaleString()}
                                    </div>
                                    <div className={`text-sm mt-1 ${
                                      shouldGreyOutProduct ? 'text-gray-500' : shouldHighlightProduct ? 'text-green-700' : 'text-gray-600'
                                    }`}>
                                      Quantity: {product.quantity} × ${product.price.toLocaleString()}
                                    </div>
                                    <div className={`text-sm mt-2 flex items-center justify-end ${
                                      shouldGreyOutProduct ? 'text-gray-500' : shouldHighlightProduct ? 'text-green-700' : 'text-gray-600'
                                    }`}>
                                      <span className="font-medium">
                                        {product.vehicle_make && product.vehicle_model 
                                          ? `${product.vehicle_make} ${product.vehicle_model}`
                                          : 'Vehicle details not available'
                                        }
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                        <div className={`p-6 ${
                          isOfferGreyedOut 
                            ? 'bg-gray-50' 
                            : isOfferSelected 
                              ? 'bg-green-50' 
                              : 'bg-gray-50'
                        }`}>
                          <div className="space-y-3">
                            {(() => {
                              // Calculate service totals
                              const serviceSubtotal = (offer.services || []).reduce((sum, service) => sum + service.price, 0);
                              const serviceDiscount = (offer.services || []).reduce((sum, service) => sum + (service.discount_amount || 0), 0);
                              
                              // Calculate product totals
                              const productSubtotal = ((offer as any).products || []).reduce((sum: number, product: any) => sum + (product.price * product.quantity), 0);
                              const productDiscount = ((offer as any).products || []).reduce((sum: number, product: any) => sum + (product.discount_amount || 0), 0);
                              
                              // Combined totals
                              const subtotal = serviceSubtotal + productSubtotal;
                              const totalDiscount = serviceDiscount + productDiscount;
                              const discountedSubtotal = subtotal - totalDiscount;
                              const gst = Math.round((discountedSubtotal * 0.05) * 100) / 100; // Round to nearest penny
                              const total = Math.round((discountedSubtotal + gst) * 100) / 100; // Round to nearest penny

                              return (
                                <>
                                  <div className={`flex justify-between text-sm ${
                                    isOfferGreyedOut ? 'text-gray-600' : isOfferSelected ? 'text-green-700' : 'text-gray-700'
                                  }`}>
                                    <span className="font-medium">Subtotal</span>
                                    <span className="font-bold">${subtotal.toLocaleString()}</span>
                                  </div>
                                  
                                  {totalDiscount > 0 && (
                                    <div className={`flex justify-between text-sm ${
                                      isOfferGreyedOut ? 'text-gray-600' : isOfferSelected ? 'text-green-700' : 'text-green-600'
                                    }`}>
                                      <span className="font-medium">Discount</span>
                                      <span className="font-bold">- ${totalDiscount.toLocaleString()}</span>
                                    </div>
                                  )}
                                  
                                  <div className={`flex justify-between text-sm ${
                                    isOfferGreyedOut ? 'text-gray-600' : isOfferSelected ? 'text-green-700' : 'text-gray-700'
                                  }`}>
                                    <span className="font-medium">GST (5%)</span>
                                    <span className="font-bold">+ ${gst.toLocaleString()}</span>
                                  </div>
                                  
                                  <div className={`flex justify-between text-lg font-bold pt-3 border-t ${
                                    isOfferGreyedOut 
                                      ? 'text-gray-700 border-gray-300' 
                                      : isOfferSelected 
                                        ? 'text-green-800 border-green-300' 
                                        : 'text-gray-900 border-gray-200'
                                  }`}>
                                    <span>Total</span>
                                    <span>${total.toLocaleString()}</span>
                                  </div>
                                </>
                              );
                            })()}
                          </div>
                      </div>
                    </div>
                    );
                  })}
                </div>
                
                  {/* Decline All Button */}
                  {localProposal.status === 'pending' && (
                    <div className="mt-6">
                      <button
                        onClick={() => {
                          localProposal.offers?.forEach(offer => handleDeclineOffer(offer));
                        }}
                        disabled={loading || readOnly}
                        className="w-full inline-flex items-center justify-center px-6 py-4 text-lg font-semibold text-red-700 bg-red-50 rounded-lg hover:bg-red-100 disabled:opacity-50 transition-colors"
                      >
                        <X className="h-6 w-6 mr-3" />
                        Decline All Offers
                      </button>
                    </div>
                  )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <CreateBookingModal
        isOpen={isConvertModalOpen}
        onClose={() => {
          setIsConvertModalOpen(false); 
        }}
        initialProposal={localProposal}
        onSuccess={async () => {
          setIsConvertModalOpen(false);
          
          // Refresh the proposal data to show the updated status
          if (proposal.id) {
            try {
              const updatedProposal = await getProposal(proposal.id);
              if (updatedProposal) {
                setLocalProposal(updatedProposal);
                // Call onUpdate if provided to notify parent component
                onUpdate?.(updatedProposal);
              }
            } catch (error) {
              console.error('Error refreshing proposal data:', error);
            }
          }
          
          onClose();
        }}
      />

      {/* Email Modal */}
      {isEmailModalOpen && (
        <ProposalEmailModal
          isOpen={isEmailModalOpen}
          onClose={() => setIsEmailModalOpen(false)}
          onSend={handleEmailSend}
          proposalTitle={localProposal.title || 'New Proposal'}
          businessName={businessName}
          customerEmail={localProposal.customer?.email || ''}
          customerFirstName={localProposal.customer?.first_name || 'there'}
          proposalId={localProposal.id}
          businessId={localProposal.business_id}
        />
      )}

      {/* SMS Modal */}
      {isSMSModalOpen && (
        <ProposalSMSModal
          isOpen={isSMSModalOpen}
          onClose={() => setIsSMSModalOpen(false)}
          onSend={handleSMSSend}
          proposalTitle={localProposal.title || 'New Proposal'}
          businessName={businessName}
          customerPhone={localProposal.customer?.phone || ''}
          customerFirstName={localProposal.customer?.first_name || 'there'}
          proposalId={localProposal.id}
          businessId={localProposal.business_id}
        />
      )}
    </>
  );

  // 🚀 FIX: Use React Portal to render modal outside parent DOM tree
  // This prevents PWA stacking context issues that cause modal to render behind navigation
  return createPortal(modalContent, document.body);
}